import { Link } from "react-router-dom";
import { useLanguage } from "@/i18n/LanguageContext";
import { Facebook, Instagram, Mail, Phone } from "lucide-react";

const Footer = () => {
  const { t, isAr } = useLanguage();

  return (
    <footer className="bg-foreground text-background">
      {/* Editorial gold ornament divider, hairline rules with centered flourish */}
      <div className="relative" aria-hidden>
        <div className="container mx-auto px-4 py-8 md:py-10">
          <div className="flex items-center justify-center gap-4 md:gap-6">
            <span className="h-px flex-1 bg-gradient-to-r from-transparent via-primary/60 to-primary/70" />
            <svg
              viewBox="0 0 80 20"
              className="h-4 md:h-5 w-auto text-primary"
              fill="none"
              stroke="currentColor"
              strokeWidth="1"
              strokeLinecap="round"
            >
              {/* Centered diamond with paired curls */}
              <path d="M2,10 C14,10 22,4 30,10 C22,16 14,10 2,10 Z" opacity="0.85" />
              <path d="M78,10 C66,10 58,4 50,10 C58,16 66,10 78,10 Z" opacity="0.85" />
              <path d="M34,10 L40,4 L46,10 L40,16 Z" fill="currentColor" opacity="0.95" />
              <circle cx="40" cy="10" r="1.2" fill="hsl(var(--background))" />
            </svg>
            <span className="h-px flex-1 bg-gradient-to-l from-transparent via-primary/60 to-primary/70" />
          </div>
        </div>
      </div>




      <div className="container mx-auto px-4 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-12">
          {/* Brand */}
          <div className="lg:col-span-1">
            <h3 className="font-serif text-2xl font-bold gold-text mb-4">NIL BEAUTY</h3>
            <p className="font-body text-sm text-background/60 leading-relaxed">{t("footer.desc")}</p>
          </div>

          {/* Explore */}
          <div>
            <h4 className="font-serif text-sm uppercase tracking-widest text-primary mb-6">{t("footer.explore")}</h4>
            <ul className="space-y-3">
              {[
                { to: "/about", label: t("footer.ourstory") },
                { to: "/shop", label: t("footer.shopall") },
                { to: "/blog", label: t("nav.blog") },
                { to: "/wholesale", label: t("nav.wholesale") },
                { to: "/contact", label: t("nav.contact") },
                { to: "/connect", label: isAr ? "الربط مع الذكاء الاصطناعي" : "Connect to AI" },
              ].map((link) => (
                <li key={link.to}>
                  <Link to={link.to} className="font-body text-sm text-background/60 hover:text-primary transition-colors duration-300">{link.label}</Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Collections */}
          <div>
            <h4 className="font-serif text-sm uppercase tracking-widest text-primary mb-6">{t("footer.collections")}</h4>
            <ul className="space-y-3">
              {[
                t("cat.soaps"), t("cat.toothpaste"), t("cat.lip-beauty"),
                t("cat.moroccan-black-soap"), t("cat.body-care"), t("cat.hair-care"),
              ].map((item) => (
                <li key={item}>
                  <Link to="/shop" className="font-body text-sm text-background/60 hover:text-primary transition-colors duration-300">{item}</Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Follow Us */}
          <div>
            <h4 className="font-serif text-sm uppercase tracking-widest text-primary mb-6">{t("footer.followus")}</h4>
            <ul className="space-y-3">
              <li>
                <a
                  href="https://www.facebook.com/NilBeautyOfficial/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 font-body text-sm text-background/60 hover:text-primary transition-colors duration-300"
                >
                  <Facebook size={18} strokeWidth={1.5}  aria-hidden="true" focusable="false" />
                  Facebook
                </a>
              </li>
              <li>
                <a
                  href="https://www.instagram.com/nilbeautyofficial/?hl=en"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 font-body text-sm text-background/60 hover:text-primary transition-colors duration-300"
                >
                  <Instagram size={18} strokeWidth={1.5}  aria-hidden="true" focusable="false" />
                  Instagram
                </a>
              </li>
            </ul>
          </div>

          {/* Contact Us */}
          <div>
            <h4 className="font-serif text-sm uppercase tracking-widest text-primary mb-6">{t("footer.contactus")}</h4>
            <ul className="space-y-3">
              <li>
                <a
                  href="mailto:info@nilbeautyofficial.com"
                  className="flex items-center gap-3 font-body text-sm text-background/60 hover:text-primary transition-colors duration-300"
                >
                  <Mail size={18} strokeWidth={1.5}  aria-hidden="true" focusable="false" />
                  info@nilbeautyofficial.com
                </a>
              </li>
              <li>
                <a
                  href="tel:+201095544676"
                  className="flex items-center gap-3 font-body text-sm text-background/60 hover:text-primary transition-colors duration-300"
                >
                  <Phone size={18} strokeWidth={1.5}  aria-hidden="true" focusable="false" />
                  01095544676
                </a>
              </li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="font-serif text-sm uppercase tracking-widest text-primary mb-6">
              {isAr ? "قانوني" : "Legal"}
            </h4>
            <ul className="space-y-3">
              {[
                { to: "/privacy", label: isAr ? "سياسة الخصوصية" : "Privacy Policy" },
                { to: "/terms", label: isAr ? "الشروط والأحكام" : "Terms & Conditions" },
                { to: "/refund", label: isAr ? "الإرجاع والاسترداد" : "Refund & Returns" },
                { to: "/shipping", label: isAr ? "الشحن والتوصيل" : "Shipping Policy" },
              ].map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    className="font-body text-sm text-background/60 hover:text-primary transition-colors duration-300"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
              <li>
                <button
                  type="button"
                  onClick={() => {
                    try { localStorage.removeItem("nb_cookie_consent_v1"); } catch {}
                    window.location.reload();
                  }}
                  className="font-body text-sm text-background/60 hover:text-primary transition-colors duration-300 text-start"
                >
                  {isAr ? "إعدادات الكوكيز" : "Cookie Settings"}
                </button>
              </li>
            </ul>
          </div>
        </div>


        <div className="mt-16 pt-8 border-t border-background/10 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="font-body text-xs text-background/60">
            © {new Date().getFullYear()} Nil Beauty. {t("footer.rights")}
          </p>
          <p className="font-body text-xs text-background/60 italic">{t("footer.crafted")}</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
